import axios from "axios";
import Config from "../utilities/Config";
const BASE_URL = `${Config.baseUrl}api/admin`;

class SenderIdRequests {
  getHeaders() {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem(
        "txtconnect_ng_admin_token"
      )}`,
    };
  }

  getFormDataHeaders() {
    return {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${localStorage.getItem(
        "txtconnect_ng_admin_token"
      )}`,
    };
  }

  async getSenderIds(
    page = 1,
    limit = 10,
    search = "",
    sortBy = "",
    sortOrder = "desc",
    status = "",
    dateFrom = "",
    dateTo = ""
  ) {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(sortBy && { sort_by: sortBy }),
        ...(sortOrder && { sort_order: sortOrder }),
        ...(status && { status }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      });

      const response = await axios.get(`${BASE_URL}/senderId?${params.toString()}`, {
        headers: this.getHeaders(),
      });
      
      return {
        success: true,
        message: response.data?.data?.message || response.data?.data?.reason || "Success",
        data: response.data?.data?.data?.data || [],
        pagination: response.data?.data?.data?.pagination || {
          total: 0,
          per_page: limit,
          current_page: page,
          last_page: 1,
          from: 1,
          to: limit,
        },
      };
    } catch (error) {
      console.error("API Error:", error);
      return {
        success: false,
        message: error.response?.data?.message || error.message,
        data: [],
        pagination: null,
      };
    }
  }

  async updateStatus(payload) {
    try {
      const response = await axios.post(`${BASE_URL}/update/status/senderId`, payload, { 
        headers: this.getHeaders() 
      });
      return {
        success: true,
        message: response.data.message || response.data.data?.message,
        data: response.data.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message,
        data: null,
      };
    }
  }

  async senderIdStats() {
    try {
      const response = await axios.get(`${BASE_URL}/statistics/senderId`, { 
        headers: this.getHeaders() 
      });
      return {
        success: true,
        message: response.data?.message || "Success",
        data: response.data?.data?.data || response.data?.data || response.data,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message,
        data: null,
      };
    }
  }

  // Export sender IDs to Excel
  async exportSenderIds(search = "", status = "", dateFrom = "", dateTo = "") {
    try {
      const params = new URLSearchParams({
        ...(search && { search }),
        ...(status && { status }),
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo && { date_to: dateTo }),
      });

      const response = await axios.get(`${BASE_URL}/export/senderId?${params.toString()}`, {
        headers: this.getHeaders(),
        responseType: 'blob', // Important for file downloads
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Try to get filename from headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let fileName = 'sender_ids_export.xlsx';
      
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return {
        success: true,
        message: 'Export started successfully',
      };
    } catch (error) {
      console.error("Export Error:", error);
      
      // If the API endpoint doesn't exist, provide a client-side export as fallback
      if (error.response?.status === 404) {
        // We'll handle the toast notification in the component, not here
        console.warn("Export API endpoint not found. Using client-side export.");
        return this.exportClientSide(search, status, dateFrom, dateTo);
      }
      
      return {
        success: false,
        message: error.response?.data?.message || "Failed to export sender IDs",
      };
    }
  }

  // Client-side export as fallback
  async exportClientSide(search = "", status = "", dateFrom = "", dateTo = "") {
    try {
      // First, get all data with current filters
      const allDataResponse = await this.getSenderIds(1, 10000, search, "", "desc", status, dateFrom, dateTo);
      
      if (!allDataResponse.success || !Array.isArray(allDataResponse.data)) {
        throw new Error("Failed to fetch data for export");
      }

      const data = allDataResponse.data;
      
      // Convert data to CSV format
      const headers = ['ID', 'Sender ID', 'User Slug', 'Status', 'Created At', 'Approved By', 'Reject Reason', 'Rejected Date'];
      
      const csvContent = [
        headers.join(','),
        ...data.map(item => [
          `"${item.id || ''}"`,
          `"${item.sender || ''}"`,
          `"${item.user_slug || ''}"`,
          `"${item.status || ''}"`,
          `"${item.created_at || ''}"`,
          `"${item.approved_by || ''}"`,
          `"${item.reject_reason || ''}"`,
          `"${item.rejected_date || ''}"`
        ].join(','))
      ].join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `sender_ids_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return {
        success: true,
        message: 'Client-side export completed',
      };
    } catch (error) {
      console.error("Client-side export error:", error);
      return {
        success: false,
        message: "Failed to export data",
      };
    }
  }

  // Alternative: Export to Excel using sheetjs if available
  async exportToExcel(search = "", status = "", dateFrom = "", dateTo = "") {
    try {
      // Check if XLSX library is available - we'll handle this in the component
      // For now, fall back to CSV
      return this.exportClientSide(search, status, dateFrom, dateTo);
    } catch (error) {
      console.error("Excel export error:", error);
      // Fall back to CSV
      return this.exportClientSide(search, status, dateFrom, dateTo);
    }
  }
}

const senderIdRequests = new SenderIdRequests();
export default senderIdRequests;